"use client";
import { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { generateSrcSet, getOptimizedImageUrl } from '@/lib/imageUtils';
import { Skeleton } from '@/components/ui/skeleton';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  priority?: boolean;
  sizes?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  placeholder?: 'blur' | 'skeleton' | string;
  quality?: number;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Unified OptimizedImage component with responsive images, lazy loading, and placeholders
 * 
 * Features:
 * - Responsive srcset for different viewport sizes
 * - Intersection Observer lazy loading (priority flag for above-the-fold)
 * - Blur gradient or skeleton placeholder options
 * - Custom placeholder image support
 * - Proper width/height for CLS prevention
 * - Supabase Storage transformation API integration
 * - Centralized image utilities
 * 
 * @example
 * ```tsx
 * <OptimizedImage
 *   src="/images/hero.jpg"
 *   alt="Hero image"
 *   width={1200}
 *   height={630}
 *   priority // For above-the-fold images
 *   sizes="(max-width: 768px) 100vw, 50vw"
 *   placeholder="blur" // or "skeleton" or custom URL
 *   quality={90}
 * />
 * ```
 */
export function OptimizedImage({
  src,
  alt,
  width,
  height,
  className,
  priority = false,
  sizes = '100vw',
  objectFit = 'cover',
  placeholder = 'blur',
  quality = 85,
  onLoad,
  onError,
}: OptimizedImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // Reset loaded state when src changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority) return;

    if ('IntersectionObserver' in window && imgRef.current) {
      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observerRef.current?.disconnect();
          }
        },
        {
          rootMargin: '50px',
          threshold: 0.1,
        }
      );

      observerRef.current.observe(imgRef.current);
    } else {
      setIsInView(true);
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [priority]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
    onError?.();
  };

  // Generate optimized src and srcset using centralized utilities
  const optimizedSrc = getOptimizedImageUrl(src, { width, quality });
  const srcSet = generateSrcSet(src, width, quality);

  return (
    <div
      ref={imgRef}
      className={cn('relative overflow-hidden bg-muted/50', className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {/* Loading placeholder */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0">
          {placeholder === 'skeleton' ? (
            <Skeleton className="w-full h-full" />
          ) : placeholder === 'blur' ? (
            <div className="absolute inset-0 bg-gradient-to-br from-muted via-muted to-muted-foreground/10 animate-pulse" />
          ) : (
            <img
              src={placeholder}
              alt={alt}
              className="w-full h-full object-cover blur-sm scale-110 transition-opacity duration-300"
            />
          )}
        </div>
      )}
      
      {/* Error fallback */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 text-muted-foreground text-sm">
          <span>Image failed to load</span>
        </div>
      )}
      
      {/* Main image - only load when in view */}
      {!hasError && isInView && (
        <img
          src={optimizedSrc}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          decoding={priority ? 'sync' : 'async'}
          onLoad={handleLoad}
          onError={handleError}
          className={cn(
            'w-full h-full transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0',
            objectFit === 'cover' && 'object-cover',
            objectFit === 'contain' && 'object-contain',
            objectFit === 'fill' && 'object-fill',
            objectFit === 'none' && 'object-none',
            objectFit === 'scale-down' && 'object-scale-down'
          )}
        />
      )}
    </div>
  );
}

/**
 * Preload a critical image (use for LCP images)
 * Call this in your page component for above-the-fold images
 */
export function preloadImage(src: string) {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = src;
  document.head.appendChild(link);
}
